import { useMemo } from 'react'

import { matchFilterKey, matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import { matchesFilters } from '@functions/relatedEntity'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { RelatedEntityModel, RelatedEntityRequestOptions } from '@models/types'

const watchEntityKeys = ['relatedEntities']

type RelatedEntityFilters = {
  data?: {
    [key: string]: any,
  },
  owner_id?: number,
  owner_type?: string,
  subject_id?: number,
  subject_type?: string,
}

type UseRelatedEntitiesOptions = {
  filters?: RelatedEntityFilters,
  performHttpRequests?: boolean,
  requestOptions?: RelatedEntityRequestOptions,
}

function useRelatedEntities(options: UseRelatedEntitiesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    data: filterData,
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
    subject_id: filterSubjectId,
    subject_type: filterSubjectType,
  } = filters

  const {
    updatedEntities: { relatedEntities: relatedEntitiesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { relatedEntities } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredRelatedEntities = useMemo(() => {
    const filtered = Object.values(relatedEntities).filter((relatedEntity: RelatedEntityModel) => {
      const {
        owner_id, owner_type, subject_type, subject_id,
      } = relatedEntity

      const matchDataFilters = matchesFilters({ filters: filterData, relatedEntity })
      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = matchFilterKey(owner_type, filterOwnerType)
      const matchSubjectId = matchFilterNumber(subject_id, filterSubjectId)
      const matchSubjectType = matchFilterKey(subject_type, filterSubjectType)

      return matchDataFilters && matchOwnerId && matchOwnerType && matchSubjectId && matchSubjectType
    })

    // @ts-expect-error TS2345
    return sortArrayBy(filtered, 'asc', 'name')
  }, [relatedEntitiesUpdatedAt, JSON.stringify(filters)])

  const filteredRelatedEntitiesCount = filteredRelatedEntities.length
  const hasFilteredRelatedEntities = !!filteredRelatedEntitiesCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredRelatedEntitiesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const { loading: loadingRelatedEntities } = useReduxAction(
    'relatedEntities',
    'loadRelatedEntities',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredRelatedEntities,
    filteredRelatedEntitiesCount,
    hasFilteredRelatedEntities,
    loading: loadingRelatedEntities,
  }
}

export default useRelatedEntities
